import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Wallet,
    ArrowRight,
    ArrowDown,
    Copy,
    CheckCircle,
    Warning,
    Cpu,
    ArrowLeft,
    ArrowUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtAone, formatApiError, shortAddr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UniversalVaultCard from "@/components/UniversalVaultCard";

/**
 * Aegis One - Wallet pages (DePIN edition).
 *
 * The wallet is USDT-first: users deposit USDT from any chain, mine AONE with it,
 * or swap AONE ⇄ USDT. Focused, no HD-derivation clutter.
 */

/* ==================================================================
 * DASHBOARD
 * ================================================================== */
export function WalletDashboardPage() {
    const { user, refresh } = useAuth();
    const [txs, setTxs] = useState([]);
    const [deposits, setDeposits] = useState([]);

    useEffect(() => {
        if (!user) return;
        refresh();
        api.get("/me/transactions", { params: { limit: 20 } }).then((r) => setTxs(r.data)).catch(() => {});
        api.get("/deposit/history").then((r) => setDeposits(r.data)).catch(() => {});
    }, [user?.id]);

    if (!user) {
        return (
            <div className="min-h-screen px-5 sm:px-8 py-16">
                <div className="max-w-md mx-auto card p-8 text-center">
                    <Wallet size={32} weight="duotone" className="text-[var(--primary)] mx-auto mb-4" />
                    <h2 className="text-[22px] font-bold text-[var(--text)]">Sign in to open wallet</h2>
                    <p className="text-[14px] text-[var(--text-2)] mt-2">
                        Your Aegis wallet holds USDT deposits and mined AONE.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <section className="border-b bg-hero-gradient" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-12 pb-8">
                    <div className="chip mb-4" data-testid="wallet-addr-chip">
                        <span className="font-mono">{shortAddr(user.address, 10, 8)}</span>
                        <button
                            onClick={() => { navigator.clipboard.writeText(user.address); toast.success("Address copied"); }}
                            className="hover:text-[var(--primary)]"
                        ><Copy size={11} /></button>
                    </div>
                    <h1 className="text-[32px] sm:text-[42px] font-bold tracking-[-0.03em] text-[var(--text)]">
                        {user.display_name || "Wallet"}
                    </h1>
                </div>
            </section>

            {/* Balances */}
            <section className="py-10 px-5 sm:px-8">
                <div className="max-w-[1100px] mx-auto grid md:grid-cols-2 gap-4">
                    <BalanceCard
                        label="USDT Balance"
                        value={`$${fmtAone(user.usdt_balance || 0, 2)}`}
                        sub="Deposited across all chains"
                        primary
                        actions={
                            <>
                                <Link data-testid="wallet-deposit-btn" to="/wallet/deposit" className="btn-primary !text-[13px]">
                                    <ArrowDown size={13} /> Deposit
                                </Link>
                                <Link data-testid="wallet-withdraw-btn" to="/wallet/withdraw" className="btn-ghost !text-[13px]">
                                    <ArrowUp size={13} /> Withdraw
                                </Link>
                                <Link to="/swap" className="btn-ghost !text-[13px]">
                                    Swap →
                                </Link>
                            </>
                        }
                    />
                    <BalanceCard
                        label="AONE Balance"
                        value={fmtAone(user.aone_balance || 0, 4)}
                        sub={`≈ $${((user.aone_balance || 0) * 0.0001).toFixed(4)} USDT`}
                        actions={
                            <>
                                <Link data-testid="wallet-mine-btn" to="/miner" className="btn-primary !text-[13px]">
                                    <Cpu size={13} /> Mine
                                </Link>
                                <Link data-testid="wallet-send-btn" to="/wallet/send" className="btn-ghost !text-[13px]">
                                    <ArrowUp size={13} /> Send
                                </Link>
                                <Link data-testid="wallet-receive-btn" to="/wallet/receive" className="btn-ghost !text-[13px]">
                                    <ArrowDown size={13} /> Receive
                                </Link>
                            </>
                        }
                    />
                </div>
            </section>

            {/* Recovery phrase */}
            <section className="pb-6 px-5 sm:px-8">
                <div className="max-w-[1100px] mx-auto">
                    <RecoveryPhraseCard />
                </div>
            </section>

            {/* Recent deposits */}
            {deposits.length > 0 && (
                <section className="pb-6 px-5 sm:px-8">
                    <div className="max-w-[1100px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-3">
                            Recent Cross-chain Deposits
                        </div>
                        <div className="card overflow-hidden">
                            <table className="tbl">
                                <thead>
                                    <tr>
                                        <th>Chain</th>
                                        <th>Amount</th>
                                        <th>Tx</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deposits.slice(0, 6).map((d) => (
                                        <tr key={d.id}>
                                            <td className="font-medium">{d.chain_name}</td>
                                            <td className="font-mono">${d.amount_usdt.toFixed(2)}</td>
                                            <td className="font-mono text-[12px]">
                                                <a href={d.explorer_url} target="_blank" rel="noreferrer">
                                                    {shortAddr(d.tx_hash, 8, 6)}
                                                </a>
                                            </td>
                                            <td><span className="chip chip-live !text-[10px] !py-0">{d.status}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {/* Recent transactions */}
            <section className="pb-16 px-5 sm:px-8">
                <div className="max-w-[1100px] mx-auto">
                    <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-3">
                        Recent Activity
                    </div>
                    <div className="card overflow-hidden">
                        <table className="tbl" data-testid="wallet-tx-table">
                            <thead>
                                <tr><th>Type</th><th>Amount</th><th>Memo</th><th>Hash</th></tr>
                            </thead>
                            <tbody>
                                {txs.length === 0 ? (
                                    <tr><td colSpan={4} className="text-center text-[var(--text-3)] py-6">No transactions yet</td></tr>
                                ) : txs.map((t) => (
                                    <tr key={t.hash}>
                                        <td><span className="chip !text-[10px] !py-0">{t.tx_type}</span></td>
                                        <td className="font-mono">{fmtAone(t.amount, 4)}</td>
                                        <td className="text-[var(--text-2)]">{t.memo || "-"}</td>
                                        <td className="font-mono text-[12px]">{shortAddr(t.hash, 8, 6)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}

/* Recovery phrase card - Trust Wallet-style seed reveal */
function RecoveryPhraseCard() {
    const [revealed, setRevealed] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const reveal = async () => {
        if (data) { setRevealed(true); return; }
        setLoading(true);
        try {
            const r = await api.get("/wallet/seed");
            setData(r.data);
            setRevealed(true);
        } catch (e) {
            toast.error(formatApiError(e, "Could not load recovery phrase"));
        } finally {
            setLoading(false);
        }
    };
    const copy = () => {
        if (!data) return;
        navigator.clipboard.writeText(data.phrase);
        toast.success("Recovery phrase copied");
    };

    return (
        <div className="card p-5 sm:p-6" data-testid="wallet-seed-card">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)]">Recovery Phrase</div>
                    <div className="text-[17px] font-semibold text-[var(--text)] mt-1">Backup your wallet</div>
                    <div className="text-[13px] text-[var(--text-2)] mt-1 max-w-lg">
                        12 words that restore your account. Write them down offline. Never share with anyone.
                    </div>
                </div>
                {!revealed ? (
                    <button data-testid="wallet-seed-reveal" onClick={reveal} disabled={loading} className="btn-primary !text-[13px]">
                        {loading ? "Loading..." : "Reveal Phrase"}
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={copy} className="btn-ghost !text-[13px]"><Copy size={13} /> Copy</button>
                        <button onClick={() => setRevealed(false)} className="btn-ghost !text-[13px]">Hide</button>
                    </div>
                )}
            </div>

            {revealed && data && (
                <>
                    <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="wallet-seed-words">
                        {data.words.map((w, i) => (
                            <div key={i} className="flex items-center gap-2 p-3 rounded-md" style={{ background: "var(--bg-raised)", border: "1px solid var(--border)" }}>
                                <span className="font-mono text-[10.5px] text-[var(--text-3)]">{String(i + 1).padStart(2, "0")}</span>
                                <span className="text-[13px] font-medium text-[var(--text)]">{w}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex items-start gap-2 text-[12px] text-[var(--warning)]">
                        <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                        <span>{data.warning}</span>
                    </div>
                </>
            )}
        </div>
    );
}

function BalanceCard({ label, value, sub, actions, primary }) {
    return (
        <div className="card p-6 sm:p-8" style={primary ? { borderColor: "var(--primary)" } : {}}>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">{label}</div>
            <div className="text-[36px] sm:text-[42px] font-bold text-[var(--text)] tabular-nums font-mono mt-2">
                {value}
            </div>
            {sub && <div className="text-[12.5px] text-[var(--text-3)] mt-1">{sub}</div>}
            <div className="flex flex-wrap gap-2 mt-6">{actions}</div>
        </div>
    );
}

/* ==================================================================
 * DEPOSIT (cross-chain USDT)
 * ================================================================== */
export function WalletDepositPage() {
    const { user, refresh } = useAuth();
    const [chains, setChains] = useState([]);
    const [vaults, setVaults] = useState([]);
    const [selected, setSelected] = useState("bep20");
    const [amount, setAmount] = useState("");
    const [txHash, setTxHash] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [showLifi, setShowLifi] = useState(false);
    const [kycOpen, setKycOpen] = useState(false);
    const [kycStatus, setKycStatus] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/deposit/chains").then((r) => setChains(r.data.chains)).catch(() => {});
        api.get("/deposit/vault").then((r) => setVaults(r.data.vaults || [])).catch(() => {});
        if (user) api.get("/kyc/status").then((r) => setKycStatus(r.data)).catch(() => {});
    }, [user?.id]);

    if (!user) {
        return (
            <div className="min-h-screen px-5 sm:px-8 py-16 text-center">
                <div className="max-w-md mx-auto card p-8">
                    <h2 className="text-[22px] font-bold text-[var(--text)]">Sign in to deposit</h2>
                </div>
            </div>
        );
    }

    const chain = chains.find((c) => c.id === selected);
    const KYC_THRESHOLD = kycStatus?.threshold_usdt || 10000;
    const needsKyc = Number(amount || 0) >= KYC_THRESHOLD && kycStatus?.status !== "verified";

    const submit = async (e) => {
        e.preventDefault();
        if (!amount || !txHash) return;
        if (needsKyc) {
            setKycOpen(true);
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/deposit/register", {
                chain: selected,
                amount_usdt: Number(amount),
                tx_hash: txHash.trim(),
            });
            toast.success(`Deposit credited · $${data.deposit.amount_usdt.toFixed(2)} USDT`);
            await refresh();
            setTimeout(() => nav("/wallet"), 700);
        } catch (e) {
            const msg = formatApiError(e, "Deposit failed");
            if (msg.toLowerCase().includes("kyc")) {
                setKycOpen(true);
            } else {
                toast.error(msg);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen">
            <section className="border-b bg-hero-gradient" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-10 pb-8">
                    <Link to="/wallet" className="text-[12px] text-[var(--text-3)] hover:text-[var(--text)] flex items-center gap-1 mb-4">
                        <ArrowLeft size={12} /> Back to wallet
                    </Link>
                    <div className="flex items-center gap-2 mb-3">
                        <span
                            data-testid="deposit-vault-eyebrow"
                            className="inline-flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded"
                            style={{
                                background: "rgba(0,229,255,0.10)",
                                color: "var(--primary-glow)",
                                border: "1px solid rgba(0,229,255,0.25)",
                            }}
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary-glow)]" style={{ boxShadow: "0 0 0 3px rgba(0,229,255,0.25)" }} />
                            Aegis Universal Vault · v1
                        </span>
                    </div>
                    <h1 className="text-[30px] sm:text-[44px] font-bold tracking-[-0.035em] text-[var(--text)]">Deposit USDT</h1>
                    <p className="mt-3 text-[15px] text-[var(--text-2)] max-w-2xl leading-[1.55]">
                        One smart contract, one address, any chain. Send USDT from BNB Smart Chain,
                        Ethereum or Polygon to the same EVM vault, or use the native Tron & Solana
                        rails — everything auto-credits to your Aegis USDT balance in ~90 seconds.
                    </p>
                </div>
            </section>

            <section className="py-10 px-5 sm:px-8">
                <div className="max-w-[1100px] mx-auto">
                    {/* Toggle: Universal Vault vs Aegis Network bridge */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        <button
                            data-testid="deposit-mode-manual"
                            onClick={() => setShowLifi(false)}
                            className={`px-4 py-2 rounded-md text-[13px] font-medium transition-colors ${!showLifi ? "bg-[var(--primary)] text-white" : "border border-[var(--border-strong)] text-[var(--text-2)]"}`}
                        >
                            Universal Vault
                        </button>
                        <button
                            data-testid="deposit-mode-lifi"
                            onClick={() => setShowLifi(true)}
                            className={`px-4 py-2 rounded-md text-[13px] font-medium transition-colors ${showLifi ? "bg-[var(--primary)] text-white" : "border border-[var(--border-strong)] text-[var(--text-2)]"}`}
                        >
                            Aegis Network Bridge (any chain)
                        </button>
                    </div>

                    {showLifi ? <LifiWidget destAddress={vaults[0]?.address || chains[0]?.address} /> : (
                        <>
                            {/* Universal Vault cards */}
                            <div className="space-y-4 mb-8">
                                {vaults.map((v) => (
                                    <UniversalVaultCard
                                        key={v.family}
                                        vault={v}
                                        featured={v.family === "evm"}
                                    />
                                ))}
                                {vaults.length === 0 && (
                                    <div className="card p-8 text-center text-[13px] text-[var(--text-3)]">
                                        Loading Aegis Universal Vault…
                                    </div>
                                )}
                            </div>

                            {/* Fast-track credit form (secondary) */}
                            <div id="fasttrack" className="card p-5 sm:p-6">
                                <div className="flex items-start justify-between gap-4 mb-4">
                                    <div>
                                        <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--text-3)]">
                                            Fast-track credit
                                        </div>
                                        <h3 className="text-[18px] font-bold text-[var(--text)] mt-1 tracking-[-0.02em]">
                                            Skip the wait — paste your tx hash
                                        </h3>
                                        <p className="text-[12.5px] text-[var(--text-2)] mt-1 leading-[1.55] max-w-xl">
                                            Already sent USDT to a vault address? Paste the source-chain tx hash
                                            below and we&apos;ll credit your balance instantly instead of waiting
                                            for the indexer.
                                        </p>
                                    </div>
                                </div>

                                {chain && (
                                    <form onSubmit={submit} className="space-y-4" data-testid="deposit-form">
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Source chain</label>
                                                <select
                                                    data-testid="deposit-chain-select"
                                                    value={selected}
                                                    onChange={(e) => setSelected(e.target.value)}
                                                    className="input mt-1"
                                                >
                                                    {chains.map((c) => (
                                                        <option key={c.id} value={c.id} data-testid={`deposit-chain-${c.id}`}>
                                                            {c.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Amount (USDT)</label>
                                                <input
                                                    data-testid="deposit-amount-input"
                                                    type="number"
                                                    min={chain.min_deposit_usdt}
                                                    step="0.01"
                                                    required
                                                    value={amount}
                                                    onChange={(e) => setAmount(e.target.value)}
                                                    placeholder="25.00"
                                                    className="input mt-1"
                                                />
                                                {needsKyc && (
                                                    <div className="mt-2 text-[12px] text-[var(--warning)]">
                                                        Deposits of ${KYC_THRESHOLD.toLocaleString()}+ require KYC. You&apos;ll be prompted on submit.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Transaction hash</label>
                                            <input
                                                data-testid="deposit-txhash-input"
                                                required
                                                value={txHash}
                                                onChange={(e) => setTxHash(e.target.value)}
                                                placeholder="0x… (paste your source-chain tx hash)"
                                                className="input mt-1 font-mono text-[12.5px]"
                                            />
                                        </div>
                                        <div className="flex items-start gap-2 text-[12px] text-[var(--warning)]">
                                            <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                                            <span>Send USDT only, on {chain.network}. Wrong network = permanent loss of funds.</span>
                                        </div>
                                        <button
                                            data-testid="deposit-submit"
                                            type="submit"
                                            disabled={submitting}
                                            className="btn-primary w-full justify-center !py-3"
                                        >
                                            {submitting ? "Crediting…" : needsKyc ? "Verify Identity → Credit" : "Credit my USDT balance"}
                                            <ArrowRight size={13} />
                                        </button>
                                    </form>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {kycOpen && <KycModal onClose={() => setKycOpen(false)} onVerified={async () => {
                setKycOpen(false);
                const r = await api.get("/kyc/status");
                setKycStatus(r.data);
                toast.success("Identity verified - you can now deposit any amount");
            }} />}
        </div>
    );
}

/* ---- Aegis Network Bridge (embedded LI.FI SDK under the hood, branded as Aegis) ---- */
function LifiWidget({ destAddress }) {
    const [Widget, setWidget] = useState(null);
    const [loadErr, setLoadErr] = useState(false);
    useEffect(() => {
        let cancelled = false;
        import("@lifi/widget")
            .then((mod) => { if (!cancelled) setWidget(() => mod.LiFiWidget); })
            .catch((e) => { console.error("Bridge widget failed", e); if (!cancelled) setLoadErr(true); });
        return () => { cancelled = true; };
    }, []);

    const config = {
        integrator: "aegis-one",
        theme: {
            palette: {
                primary: { main: "#2563EB" },
                secondary: { main: "#00E5FF" },
            },
        },
        appearance: "dark",
        variant: "compact",
        toChain: 56,
        toToken: "0x55d398326f99059fF775485246999027B3197955",
        hiddenUI: ["poweredBy", "history"],
    };

    return (
        <div className="card p-4 sm:p-6" data-testid="lifi-widget-iframe">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-[var(--primary-glow)] mb-2 flex items-center gap-2 px-1">
                <span className="w-2 h-2 rounded-full bg-[var(--primary-glow)]" style={{ boxShadow: "0 0 0 3px rgba(0,229,255,0.25)" }} />
                Aegis Network Bridge — Any Chain to USDT
            </div>
            <p className="text-[12.5px] text-[var(--text-2)] px-1 pb-4 leading-snug max-w-2xl">
                Route USDT from 20+ chains through the Aegis Network liquidity mesh. Confirm the destination transaction, then paste the hash on the Manual tab to credit your Aegis USDT balance.
            </p>
            <div style={{ minHeight: 620 }}>
                {loadErr ? (
                    <div className="flex flex-col items-center justify-center h-[420px] gap-3 text-center text-[13px] text-[var(--text-3)]">
                        Bridge failed to load. Use the Manual tab to register deposits.
                    </div>
                ) : Widget ? <Widget config={config} /> : (
                    <div className="flex items-center justify-center h-[420px] text-[13px] text-[var(--text-3)]">
                        Loading Aegis Network Bridge...
                    </div>
                )}
            </div>
            <div className="mt-3 text-[10.5px] font-mono uppercase tracking-[0.14em] text-[var(--text-4)]">
                Routing engine · powered by Aegis Network
            </div>
        </div>
    );
}

/* ---- KYC Modal (light) ---- */
function KycModal({ onClose, onVerified }) {
    const [form, setForm] = useState({ legal_name: "", country: "", document_type: "passport", document_number: "" });
    const [submitting, setSubmitting] = useState(false);
    const [awaiting, setAwaiting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/kyc/submit", form);
            setAwaiting(true);
            // Preview: auto-verified after ~15s
            const check = setInterval(async () => {
                const r = await api.get("/kyc/status");
                if (r.data.status === "verified") {
                    clearInterval(check);
                    onVerified();
                }
            }, 3000);
            setTimeout(() => clearInterval(check), 25000);
        } catch (e) {
            toast.error(formatApiError(e, "KYC submission failed"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="kyc-modal"
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(5,7,14,0.7)", backdropFilter: "blur(8px)" }}
            onClick={onClose}
        >
            <div
                className="card p-6 sm:p-8 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="eyebrow mb-2">Verify Identity</div>
                <h3 className="text-[22px] font-bold text-[var(--text)]">Light KYC required</h3>
                <p className="text-[13.5px] text-[var(--text-2)] mt-2 leading-[1.55]">
                    Deposits of $10,000+ require basic identity verification. Preview: auto-approves in ~15 seconds.
                </p>

                {awaiting ? (
                    <div className="mt-6 py-8 text-center">
                        <div className="inline-block w-6 h-6 rounded-full border-2 border-[var(--primary)] border-t-transparent animate-spin" />
                        <div className="mt-4 text-[14px] text-[var(--text-2)]">Verifying your details…</div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-5 space-y-4">
                        <input
                            data-testid="kyc-legalname"
                            required minLength={2}
                            value={form.legal_name}
                            onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
                            placeholder="Legal name"
                            className="input"
                        />
                        <input
                            data-testid="kyc-country"
                            required minLength={2}
                            value={form.country}
                            onChange={(e) => setForm({ ...form, country: e.target.value })}
                            placeholder="Country (e.g. India)"
                            className="input"
                        />
                        <select
                            data-testid="kyc-doctype"
                            value={form.document_type}
                            onChange={(e) => setForm({ ...form, document_type: e.target.value })}
                            className="input"
                        >
                            <option value="passport">Passport</option>
                            <option value="national_id">National ID</option>
                            <option value="drivers_license">Driver&apos;s License</option>
                        </select>
                        <input
                            data-testid="kyc-docnumber"
                            required minLength={3}
                            value={form.document_number}
                            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                            placeholder="Document number"
                            className="input font-mono text-[13px]"
                        />
                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
                            <button data-testid="kyc-submit" type="submit" disabled={submitting} className="btn-primary flex-1 justify-center">
                                {submitting ? "Submitting…" : "Submit"} <ArrowRight size={13} />
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

/* ==================================================================
 * SEND - Trust Wallet-style AONE send with gas fee
 * ================================================================== */
export function WalletSendPage() {
    const { user, refresh } = useAuth();
    const [to, setTo] = useState("");
    const [amount, setAmount] = useState("");
    const [memo, setMemo] = useState("");
    const [gas, setGas] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/wallet/gas").then((r) => setGas(r.data)).catch(() => {});
    }, []);

    if (!user) return <RequireSignIn />;

    const totalCost = Number(amount || 0) + Number(gas?.gas_fee_aone || 0);
    const insufficient = totalCost > Number(user.aone_balance || 0);

    const send = async (e) => {
        e.preventDefault();
        if (!to || !amount) return;
        setBusy(true);
        try {
            const { data } = await api.post("/wallet/send", { to_address: to.trim(), amount: Number(amount), memo });
            setResult(data);
            toast.success(`Sent ${data.amount} AONE`);
            await refresh();
        } catch (e) {
            toast.error(formatApiError(e, "Send failed"));
        } finally {
            setBusy(false);
        }
    };

    if (result) {
        return (
            <div className="min-h-screen px-5 sm:px-8 py-16">
                <div className="max-w-[520px] mx-auto card p-8 text-center">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "var(--primary-soft)", border: "1px solid var(--primary-ring)" }}>
                        <CheckCircle size={26} weight="fill" className="text-[var(--primary-glow)]" />
                    </div>
                    <h2 className="text-[26px] font-bold text-[var(--text)]">Transaction sent</h2>
                    <p className="text-[14px] text-[var(--text-2)] mt-2">
                        {result.amount} AONE + {result.gas_fee} gas
                    </p>
                    <div className="card p-4 mt-6 text-left" style={{ background: "var(--bg-raised)" }}>
                        <Row label="To" value={shortAddr(result.recipient, 12, 10)} mono />
                        <Row label="Amount" value={`${result.amount} AONE`} />
                        <Row label="Gas fee" value={`${result.gas_fee} AONE`} />
                        <Row label="Tx hash" value={shortAddr(result.tx_hash, 12, 10)} mono />
                        <Row label="New balance" value={`${result.new_balance.toFixed(4)} AONE`} />
                    </div>
                    <div className="flex gap-2 mt-6">
                        <button onClick={() => { setResult(null); setTo(""); setAmount(""); setMemo(""); }} className="btn-ghost flex-1 justify-center">Send another</button>
                        <button onClick={() => nav("/wallet")} className="btn-primary flex-1 justify-center">Back to Wallet</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen px-5 sm:px-8 py-10">
            <div className="max-w-[560px] mx-auto">
                <Link to="/wallet" className="text-[12px] text-[var(--text-3)] hover:text-[var(--text)] flex items-center gap-1 mb-4">
                    <ArrowLeft size={12} /> Back to wallet
                </Link>
                <h1 className="text-[30px] font-bold text-[var(--text)]">Send AONE</h1>
                <p className="text-[13.5px] text-[var(--text-3)] mt-2">
                    Balance: <span className="font-mono text-[var(--text)]">{fmtAone(user.aone_balance, 4)} AONE</span>
                </p>
                <form onSubmit={send} className="card p-5 space-y-4 mt-6" data-testid="wallet-send-form">
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Recipient</label>
                        <input
                            data-testid="wallet-send-to"
                            value={to} onChange={(e) => setTo(e.target.value)}
                            placeholder="aegis1..."
                            className="input mt-1 font-mono text-[12.5px]"
                            required
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Amount (AONE)</label>
                        <input
                            data-testid="wallet-send-amount"
                            type="number" min={gas?.min_send_aone || 0.0001} step="0.0001"
                            value={amount} onChange={(e) => setAmount(e.target.value)}
                            className="input mt-1"
                            required
                        />
                    </div>
                    <div>
                        <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Memo (optional)</label>
                        <input
                            value={memo} onChange={(e) => setMemo(e.target.value)}
                            placeholder="Transaction note"
                            className="input mt-1"
                            maxLength={140}
                        />
                    </div>
                    {gas && (
                        <div className="p-3 rounded-md text-[13px] space-y-1" style={{ background: "var(--bg-raised)", border: "1px solid var(--border)" }}>
                            <Row label="Gas fee" value={`${gas.gas_fee_aone} AONE`} small />
                            <Row label="Total cost" value={`${totalCost.toFixed(4)} AONE`} small strong />
                        </div>
                    )}
                    {insufficient && amount && (
                        <div className="text-[12px] text-[var(--danger)]">Insufficient balance for amount + gas.</div>
                    )}
                    <button
                        data-testid="wallet-send-submit"
                        disabled={busy || insufficient}
                        className="btn-primary w-full justify-center !py-3"
                    >
                        {busy ? "Sending..." : "Send AONE"} <ArrowRight size={13} />
                    </button>
                </form>
            </div>
        </div>
    );
}

function Row({ label, value, mono, small, strong }) {
    return (
        <div className={`flex justify-between ${small ? "" : "py-1.5"}`}>
            <span className="text-[var(--text-3)]">{label}</span>
            <span className={`${mono ? "font-mono" : ""} ${strong ? "font-semibold text-[var(--text)]" : "text-[var(--text)]"}`}>{value}</span>
        </div>
    );
}

/* ==================================================================
 * RECEIVE - show address
 * ================================================================== */
export function WalletReceivePage() {
    const { user } = useAuth();
    if (!user) return <RequireSignIn />;

    return (
        <div className="min-h-screen px-5 sm:px-8 py-10">
            <div className="max-w-[560px] mx-auto">
                <Link to="/wallet" className="text-[12px] text-[var(--text-3)] hover:text-[var(--text)] flex items-center gap-1 mb-4">
                    <ArrowLeft size={12} /> Back
                </Link>
                <h1 className="text-[28px] font-bold text-[var(--text)]">Receive AONE</h1>
                <p className="text-[14px] text-[var(--text-2)] mt-3">
                    Share this address to receive AONE on the Aegis L1.
                </p>
                <div className="card p-5 mt-6">
                    <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-2">Your Aegis Address</div>
                    <div className="flex items-center gap-2 p-3 rounded-md" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)" }}>
                        <code className="font-mono text-[13px] flex-1 break-all">{user.address}</code>
                        <button
                            onClick={() => { navigator.clipboard.writeText(user.address); toast.success("Address copied"); }}
                            className="p-1.5 rounded-md hover:bg-[var(--bg)]"
                        ><Copy size={14} /></button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function RequireSignIn() {
    return (
        <div className="min-h-screen px-5 sm:px-8 py-16 text-center">
            <div className="max-w-md mx-auto card p-8">
                <h2 className="text-[22px] font-bold text-[var(--text)]">Sign in required</h2>
                <Link to="/wallet" className="link-accent mt-4 inline-flex">Back to wallet →</Link>
            </div>
        </div>
    );
}

/* ==================================================================
 * WITHDRAW USDT - to any external wallet across chains
 * ================================================================== */
export function WalletWithdrawPage() {
    const { user, refresh } = useAuth();
    const [chains, setChains] = useState([]);
    const [chain, setChain] = useState("bep20");
    const [addr, setAddr] = useState("");
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    const nav = useNavigate();

    useEffect(() => {
        api.get("/wallet/withdraw/chains").then((r) => setChains(r.data.chains)).catch(() => {});
        if (user) loadHistory();
    }, [user?.id]);

    const loadHistory = async () => {
        try {
            const r = await api.get("/wallet/withdrawals");
            setHistory(r.data.withdrawals || []);
        } catch { /* ignore */ }
    };

    if (!user) return <RequireSignIn />;

    const selected = chains.find((c) => c.id === chain);
    const bal = Number(user.usdt_balance || 0);
    const amt = Number(amount || 0);
    const netFee = selected?.network_fee_usdt || 0;
    const platformFee = amt * 0.005;
    const total = amt + netFee;
    const netReceive = amt - platformFee;
    const insufficient = total > bal;

    const submit = async (e) => {
        e.preventDefault();
        if (!addr || !amount) return;
        setBusy(true);
        try {
            const { data } = await api.post("/wallet/withdraw", {
                chain,
                wallet_address: addr.trim(),
                amount_usdt: amt,
            });
            setResult(data.withdrawal);
            toast.success("Withdrawal submitted");
            await refresh();
            await loadHistory();
        } catch (e) {
            toast.error(formatApiError(e, "Withdrawal failed"));
        } finally {
            setBusy(false);
        }
    };

    if (result) {
        return (
            <div className="min-h-screen px-5 sm:px-8 py-16">
                <div className="max-w-[560px] mx-auto card p-8 text-center">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "var(--primary-soft)", border: "1px solid var(--primary-ring)" }}>
                        <CheckCircle size={26} weight="fill" className="text-[var(--primary-glow)]" />
                    </div>
                    <h2 className="text-[26px] font-bold text-[var(--text)]">Withdrawal submitted</h2>
                    <p className="text-[14px] text-[var(--text-2)] mt-2">
                        {result.chain_name} · settles in ~{Math.round((new Date(result.estimated_completion_at) - new Date()) / 1000)}s
                    </p>
                    <div className="card p-4 mt-6 text-left" style={{ background: "var(--bg-raised)" }}>
                        <Row label="To wallet" value={result.wallet_masked} mono />
                        <Row label="Chain" value={result.chain_name} />
                        <Row label="Amount" value={`$${result.amount_usdt.toFixed(2)} USDT`} />
                        <Row label="Net received" value={`$${result.net_amount_usdt.toFixed(2)} USDT`} strong />
                        <Row label="Platform fee" value={`$${result.platform_fee_usdt.toFixed(2)}`} />
                        <Row label="Network fee" value={`$${result.network_fee_usdt.toFixed(2)}`} />
                        <Row label="Tx hash" value={shortAddr(result.tx_hash, 12, 10)} mono />
                    </div>
                    <div className="mt-4">
                        <a
                            href={result.explorer_url}
                            target="_blank"
                            rel="noreferrer"
                            className="link-accent text-[13px] inline-flex items-center gap-1"
                            data-testid="withdraw-explorer-link"
                        >
                            View on explorer <ArrowRight size={12} />
                        </a>
                    </div>
                    <div className="flex gap-2 mt-6">
                        <button onClick={() => { setResult(null); setAddr(""); setAmount(""); }} className="btn-ghost flex-1 justify-center">Withdraw more</button>
                        <button onClick={() => nav("/wallet")} className="btn-primary flex-1 justify-center">Back to Wallet</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <section className="border-b bg-hero-gradient" style={{ borderColor: "var(--border)" }}>
                <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-10 pb-8">
                    <Link to="/wallet" className="text-[12px] text-[var(--text-3)] hover:text-[var(--text)] flex items-center gap-1 mb-4">
                        <ArrowLeft size={12} /> Back to wallet
                    </Link>
                    <h1 className="text-[30px] sm:text-[44px] font-bold tracking-[-0.035em] text-[var(--text)]">Withdraw USDT</h1>
                    <p className="mt-3 text-[15px] text-[var(--text-2)] max-w-2xl leading-[1.55]">
                        Send your USDT balance to any external wallet across Tron, BNB Chain, Ethereum or Polygon. Settlement is on-chain with a live tx hash and explorer link.
                    </p>
                </div>
            </section>

            <section className="py-10 px-5 sm:px-8">
                <div className="max-w-[1100px] mx-auto grid lg:grid-cols-5 gap-6">
                    <div className="lg:col-span-2 card p-4">
                        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] px-2 py-2">
                            Destination chain
                        </div>
                        <div className="space-y-1">
                            {chains.map((c) => (
                                <button
                                    key={c.id}
                                    data-testid={`withdraw-chain-${c.id}`}
                                    onClick={() => setChain(c.id)}
                                    className={`w-full text-left flex items-center justify-between px-3 py-2.5 rounded-md transition-colors ${
                                        chain === c.id
                                            ? "bg-[var(--primary-soft)] text-[var(--primary-bright)]"
                                            : "hover:bg-[var(--bg-raised)] text-[var(--text)]"
                                    }`}
                                >
                                    <div>
                                        <div className="text-[13.5px] font-medium">{c.name}</div>
                                        <div className="text-[11px] text-[var(--text-3)]">fee ${c.network_fee_usdt.toFixed(2)} · ~{c.estimated_settle_seconds}s</div>
                                    </div>
                                    {chain === c.id && <CheckCircle size={16} weight="fill" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <div className="card p-5 mb-4">
                            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-2">
                                Available USDT balance
                            </div>
                            <div className="text-[28px] font-bold text-[var(--text)] font-mono tabular-nums">
                                ${fmtAone(bal, 2)}
                            </div>
                        </div>
                        <form onSubmit={submit} className="card p-5 space-y-4" data-testid="withdraw-form">
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Recipient wallet address</label>
                                <input
                                    data-testid="withdraw-address"
                                    value={addr}
                                    onChange={(e) => setAddr(e.target.value)}
                                    placeholder={chain === "trc20" ? "T..." : "0x..."}
                                    className="input mt-1 font-mono text-[12.5px]"
                                    required
                                />
                                <p className="text-[11px] text-[var(--text-3)] mt-1.5">
                                    {chain === "trc20"
                                        ? "Tron address (starts with T)."
                                        : "EVM address (0x...) - must be a wallet you control on the selected chain."}
                                </p>
                            </div>
                            <div>
                                <label className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)]">Amount (USDT)</label>
                                <input
                                    data-testid="withdraw-amount"
                                    type="number" min={5} max={bal} step="0.01"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="25.00"
                                    className="input mt-1"
                                    required
                                />
                            </div>
                            {amt > 0 && (
                                <div className="p-3 rounded-md text-[13px] space-y-1" style={{ background: "var(--bg-raised)", border: "1px solid var(--border)" }}>
                                    <Row label="Amount" value={`$${amt.toFixed(2)}`} small />
                                    <Row label="Platform fee (0.5%)" value={`- $${platformFee.toFixed(2)}`} small />
                                    <Row label={`${selected?.name || "Chain"} network fee`} value={`- $${netFee.toFixed(2)}`} small />
                                    <Row label="Total debited" value={`$${total.toFixed(2)}`} small strong />
                                    <Row label="Recipient will receive" value={`$${Math.max(0, netReceive).toFixed(2)}`} small strong />
                                </div>
                            )}
                            {insufficient && amount && (
                                <div className="text-[12px] text-[var(--danger)]">Insufficient USDT balance for amount + network fee.</div>
                            )}
                            <button
                                data-testid="withdraw-submit"
                                disabled={busy || insufficient || amt < 5}
                                className="btn-primary w-full justify-center !py-3"
                            >
                                {busy ? "Submitting..." : "Withdraw USDT"} <ArrowRight size={13} />
                            </button>
                            <div className="flex items-start gap-2 text-[11.5px] text-[var(--text-3)] leading-[1.55]">
                                <Warning size={13} weight="fill" className="shrink-0 mt-0.5 text-[var(--warning)]" />
                                <span>
                                    Double-check the address and chain before submitting. Cross-chain sends to the wrong network are permanent and cannot be reversed.
                                </span>
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            {history.length > 0 && (
                <section className="pb-16 px-5 sm:px-8">
                    <div className="max-w-[1100px] mx-auto">
                        <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-[var(--text-3)] mb-3">
                            Withdrawal History
                        </div>
                        <div className="card overflow-hidden">
                            <table className="tbl" data-testid="withdraw-history-table">
                                <thead>
                                    <tr>
                                        <th>Chain</th>
                                        <th>Amount</th>
                                        <th>To</th>
                                        <th>Tx</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((w) => (
                                        <tr key={w.id}>
                                            <td className="font-medium">{w.chain_name}</td>
                                            <td className="font-mono">${w.amount_usdt.toFixed(2)}</td>
                                            <td className="font-mono text-[12px]">{w.wallet_masked}</td>
                                            <td className="font-mono text-[12px]">
                                                <a href={w.explorer_url} target="_blank" rel="noreferrer" className="hover:text-[var(--primary)]">
                                                    {shortAddr(w.tx_hash, 8, 6)}
                                                </a>
                                            </td>
                                            <td>
                                                <span className={`chip !text-[10px] !py-0 ${w.status === "completed" ? "chip-live" : ""}`}>
                                                    {w.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
